import { ReloadOutlined } from "@ant-design/icons"
import { Alert, Button, Card, Select, Table } from "antd"
import { useCallback } from "react"
import { Link } from "react-router-dom"
import type { PointRevision } from "../../types/regulatory"
import { allCatalogPages, regulatoryApi } from "../../utils/regulatoryApi"
import {
  CatalogFiltersBar,
  CatalogFrame,
  NatureTag,
  PointDetails,
  ResourceState,
} from "./components/CatalogUI"
import { useCatalogFilters, useCatalogResource } from "./useCatalog"
export function ControlPointsPage() {
  const { filters, page, update } = useCatalogFilters()
  const resource = useCatalogResource(
    useCallback((signal) => regulatoryApi.points(filters, signal), [filters]),
  )
  const selectors = useCatalogResource(
    useCallback(async (signal) => {
      const [templates, packs] = await Promise.all([
        allCatalogPages(regulatoryApi.templates, signal),
        allCatalogPages(regulatoryApi.packs, signal),
      ])
      return { templates, packs }
    }, []),
  )
  return (
    <CatalogFrame
      title="Points de contrôle"
      extra={
        <Button icon={<ReloadOutlined />} onClick={resource.reload}>
          Actualiser
        </Button>
      }
    >
      <CatalogFiltersBar filters={filters} update={update}>
        <Select
          aria-label="Modèle"
          placeholder="Tous les modèles"
          allowClear
          showSearch
          optionFilterProp="label"
          value={filters.templateUuid}
          onChange={(value) => update({ templateUuid: value })}
          loading={selectors.loading}
          options={selectors.data?.templates.map((template) => ({
            value: template.uuid,
            label: template.name,
          }))}
          style={{ minWidth: 230, maxWidth: 360 }}
        />
        <Select
          aria-label="Paquet source"
          placeholder="Tous les paquets sources"
          allowClear
          value={filters.packUuid}
          onChange={(value) => update({ packUuid: value })}
          loading={selectors.loading}
          options={selectors.data?.packs.map((pack) => ({
            value: pack.uuid,
            label: pack.externalId,
          }))}
          style={{ minWidth: 230 }}
        />
      </CatalogFiltersBar>
      {selectors.error && (
        <Alert
          type="warning"
          showIcon
          message="Les filtres de modèle et de source n’ont pas pu être chargés."
          description={selectors.error.message}
          action={<Button onClick={selectors.reload}>Réessayer</Button>}
        />
      )}
      <ResourceState {...resource} retry={resource.reload}>
        <Card>
          <Table<PointRevision>
            rowKey="uuid"
            dataSource={resource.data?.items ?? []}
            scroll={{ x: 800 }}
            pagination={{
              current: page,
              pageSize: filters.limit,
              total: resource.data?.total,
              showSizeChanger: true,
              pageSizeOptions: [25, 50, 100, 200],
              showTotal: (total) => `${total} points`,
              onChange: (next, size) => update({ page: next, limit: size }),
            }}
            columns={[
              { title: "Code", render: (_value, point) => point.definition.controlCode ?? "—" },
              { title: "Section", render: (_value, point) => point.definition.section },
              {
                title: "Point de contrôle",
                render: (_value, point) => (
                  <Link to={`/regulatory/points/${point.uuid}`}>{point.definition.label}</Link>
                ),
              },
              {
                title: "Nature",
                render: (_value, point) => <NatureTag point={point.definition} />,
              },
              { title: "Référence", render: (_value, point) => point.definition.reference },
            ]}
            expandable={{ expandedRowRender: (point) => <PointDetails point={point} /> }}
          />
        </Card>
      </ResourceState>
    </CatalogFrame>
  )
}
